import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import type { AdminUser } from "@/api/hooks";
import { formatDate } from "@/i18n/format";
import { extractApiDetail } from "@/lib/apiDetail";

/** Whether the typed confirmation matches: trimmed, case-insensitive. */
export function confirmationMatches(typed: string, expected: string): boolean {
  return typed.trim().toLocaleLowerCase() === expected.trim().toLocaleLowerCase();
}

/** What an account removal deletes and keeps (Privacy Policy 10.1-10.2). */
export function useAccountRemovalLists(): { deleted: string[]; kept: string[] } {
  const { t } = useTranslation();
  return {
    deleted: [
      t("removal.user.profile"),
      t("removal.user.sign_in"),
      t("removal.user.accounts"),
      t("removal.user.drafts"),
      t("removal.user.memberships"),
      t("removal.user.feedback"),
    ],
    kept: [t("removal.user.kept_content"), t("removal.user.kept_records")],
  };
}

/** What an organisation removal deletes and keeps (Privacy Policy 10.4). */
export function useOrgRemovalLists(): { deleted: string[]; kept: string[] } {
  const { t } = useTranslation();
  return {
    deleted: [
      t("removal.org.sessions"),
      t("removal.org.files"),
      t("removal.org.brand"),
      t("removal.org.sources"),
      t("removal.org.access"),
    ],
    kept: [t("removal.org.kept_accounts")],
  };
}

/** The org slugs a 409 ``{code: "sole_admin", orgs}`` names, or null. */
export function soleAdminOrgs(err: unknown): string[] | null {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response
    ?.data?.detail as { code?: unknown; orgs?: unknown } | undefined;
  if (detail && detail.code === "sole_admin" && Array.isArray(detail.orgs)) {
    return detail.orgs.map(String);
  }
  return null;
}

/** Whether the signed-in platform admin may remove ``user``'s account
 *  (Terms 19.3): not their own (that is Account), not a removed one; an
 *  admin removes plain users only, a superadmin anyone. The backend
 *  checks the same. */
export function canRemoveUser(
  user: AdminUser,
  actingRole: string,
  actingId: string | undefined,
): boolean {
  if (user.deleted || user.id === actingId) return false;
  if (actingRole === "superadmin") return true;
  return actingRole === "admin" && user.role === "user";
}

/** Whether a removal was refused because a paid plan is still live: the
 *  plan is cancelled first (409 ``org_has_paid_plan``). */
export function orgHasPaidPlan(err: unknown): boolean {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response
    ?.data?.detail as { code?: unknown } | undefined;
  return Boolean(detail && detail.code === "org_has_paid_plan");
}

/** The text for a failed organisation removal: the 409 that a turn is
 *  still running names the sessions; a live paid plan says to cancel it
 *  first; anything else as the API said it. */
export function orgRemovalError(t: TFunction, err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response
    ?.data?.detail as
    | { code?: unknown; sessions?: unknown; plan?: unknown; ends_at?: unknown }
    | undefined;
  if ((detail as unknown) === "org_delete_retry") return t("removal.org.retry");
  if (detail && detail.code === "org_has_paid_plan") {
    const plan = typeof detail.plan === "string" ? detail.plan : "";
    return typeof detail.ends_at === "string"
      ? t("removal.org.paid_plan_ending", { plan, date: formatDate(detail.ends_at) })
      : t("removal.org.paid_plan", { plan });
  }
  if (detail && detail.code === "org_has_active_sessions" && Array.isArray(detail.sessions)) {
    const titles = (detail.sessions as Array<{ title?: unknown }>)
      .map((row) => (typeof row.title === "string" && row.title ? row.title : "…"))
      .join(", ");
    return t("removal.org.active_sessions", { titles });
  }
  return extractApiDetail(err);
}
